/**
 * Hooks Registry - Single Source of Truth
 *
 * 统一的 hooks 和功能配置注册表，替代分散在多个文件中的配置定义。
 *
 * 本文件合并了以下分散配置：
 * - config/featureHooks.ts (已废弃)
 * - features/hooks/HooksPipeline.tsx 中的 VIRTUAL_FEATURE_HOOKS, HOOK_TO_TAB_MAPPING
 * - pages/AgentConfigPage.tsx 中的 FEATURE_PATH_TO_TAB, TAB_TO_FEATURE_CONFIG, TAB_TO_HOOK_NAME
 *
 * Model capability hooks (vision, stt, reasoning, reranking) are auto-generated
 * from backend schema. See: ./generated/hooksRegistry.generated.ts
 * Regenerate with: pnpm run generate:config
 *
 * @module hooks-registry
 */

import type { TabId } from './tabConfig';
import { GENERATED_MODEL_HOOKS } from './generated/hooksRegistry.generated';

// ============= Type Definitions =============

export type FeatureGroupId = 'core' | 'models' | 'memory' | 'astrology' | 'skills' | 'knowledge' | 'advanced';

export type HookId =
  // Core (感知模块)
  | 'storage'
  | 'datetime'
  | 'device'
  | 'location'
  | 'emotion_enhance'
  // Models (模型能力) - configPath uses hooks.*.enabled pattern
  | 'quick_chat'
  | 'vision'     // hooks.vision.enabled
  | 'reasoning'  // hooks.reasoning.enabled
  | 'reranking'  // hooks.reranking.enabled
  | 'stt'        // hooks.stt.enabled
  // Memory (all memory-related)
  | 'memory_save'
  | 'user_facts'
  | 'memory_context'
  | 'memory_reinforce'
  // Astrology
  | 'astrology_plus'
  // Skills & Classifier
  | 'classifier'
  | 'skill_dedup'
  | 'skill_tracking'
  // Knowledge
  | 'knowledge_context'
  // Advanced
  | 'first_message'
  | 'team'
  | 'debug';

export type PresetId = 'minimal' | 'standard' | 'advanced' | 'custom';
/** Selectable preset IDs (excludes 'custom' which is inferred, not user-clickable) */
export type SelectablePresetId = 'minimal' | 'standard' | 'advanced';

export interface FeatureGroup {
  id: FeatureGroupId;
  name: string;
  description: string;
  icon: string;
  order: number;
  /** 是否始终启用（核心基础） */
  alwaysEnabled?: boolean;
  /** 默认是否展开 */
  defaultExpanded?: boolean;
}

export interface HookDefinition {
  id: HookId;
  displayName: string;
  description: string;
  group: FeatureGroupId;
  /** 配置路径，用于读取/写入启用状态 */
  configPath: string;
  /** 关联的配置 Tab */
  linkedTab?: TabId;
  /** 关联的配置 Section（用于高亮跳转） */
  linkedSection?: string;
  /** 是否为虚拟 hook（无后端对应） */
  isVirtual?: boolean;
  /** 直接切换模式（点击即启用/禁用，无需展开 Tab） */
  directToggle?: boolean;
  /** 依赖的 hooks（启用此 hook 前需要启用的 hooks） */
  requires?: HookId[];
  /** 依赖此 hook 的 hooks（禁用此 hook 时需要确认） */
  dependents?: HookId[];
  /** 显示顺序（在功能组内） */
  order?: number;
  /** 图标名称 */
  icon?: string;
  /** 是否增强系统提示词（注入上下文到 System Prompt） */
  enhancesPrompt?: boolean;
}

export interface ConfigPreset {
  id: SelectablePresetId;
  name: string;
  description: string;
  isDefault?: boolean;
  enabledHooks: HookId[];
}

// ============= Feature Groups =============

export const FEATURE_GROUPS: Record<FeatureGroupId, FeatureGroup> = {
  core: {
    id: 'core',
    name: '感知模块',
    description: '让 AI 感知对话上下文、时间和用户情绪',
    icon: 'Activity',
    order: 10,
    alwaysEnabled: true,
    defaultExpanded: true,
  },
  models: {
    id: 'models',
    name: '模型能力',
    description: '视觉、推理、语音等扩展能力',
    icon: 'Cpu',
    order: 15,
    defaultExpanded: true,
  },
  memory: {
    id: 'memory',
    name: '记忆系统',
    description: '长期记忆、用户画像、记忆检索与强化',
    icon: 'Brain',
    order: 20,
    defaultExpanded: true,
  },
  astrology: {
    id: 'astrology',
    name: '占星能力',
    description: '本命盘计算、行运预测、星座分析',
    icon: 'Sparkles',
    order: 30,
    defaultExpanded: true,
  },
  skills: {
    id: 'skills',
    name: '技能',
    description: '技能匹配、追踪与去重',
    icon: 'Zap',
    order: 50,
    defaultExpanded: false,
  },
  knowledge: {
    id: 'knowledge',
    name: '知识检索',
    description: 'RAG 知识库检索增强生成',
    icon: 'BookOpen',
    order: 60,
    defaultExpanded: false,
  },
  advanced: {
    id: 'advanced',
    name: '高级选项',
    description: '团队模式、调试工具、自定义首消息',
    icon: 'Settings',
    order: 100,
    defaultExpanded: false,
  },
};

// ============= Hooks Registry =============

export const HOOKS_REGISTRY: Record<HookId, HookDefinition> = {
  // ===== Core (感知模块) =====
  storage: {
    id: 'storage',
    displayName: '上下文感知',
    description: '感知对话历史，支持多轮对话上下文理解',
    group: 'core',
    configPath: 'storage_enabled',
    isVirtual: true,
    directToggle: true,
    icon: 'MessageSquare',
    order: 10,
    enhancesPrompt: true,
  },
  datetime: {
    id: 'datetime',
    displayName: '时间感知',
    description: '感知当前日期和时间，让 AI 具有时间意识',
    group: 'core',
    configPath: 'context.datetime_enabled',
    isVirtual: true,
    directToggle: true,
    icon: 'Clock',
    order: 20,
    enhancesPrompt: true,
  },
  device: {
    id: 'device',
    displayName: '设备感知',
    description: '感知用户当前使用的设备类型（手机/平板/电脑）',
    group: 'core',
    configPath: 'context.device_enabled',
    isVirtual: true,
    directToggle: true,
    icon: 'Layers',
    order: 30,
    enhancesPrompt: true,
  },
  location: {
    id: 'location',
    displayName: '位置感知',
    description: '感知用户当前地理位置，提供本地化服务',
    group: 'core',
    configPath: 'context.location_enabled',
    isVirtual: true,
    directToggle: true,
    icon: 'MapPin',
    order: 40,
    enhancesPrompt: true,
  },
  emotion_enhance: {
    id: 'emotion_enhance',
    displayName: '情绪感知',
    description: '感知用户情绪状态和意图，提供更有温度的共情回复',
    group: 'core',
    configPath: 'hooks.emotion_enhance.enabled',
    linkedTab: 'emotion',
    directToggle: true,
    icon: 'Heart',
    order: 50,
    enhancesPrompt: true,
  },

  // ===== Models (模型能力) =====
  // These hooks use the new unified hooks.*.enabled pattern for enabling/disabling
  // Model selection is separate from enabling (models.*.model_id)
  quick_chat: {
    id: 'quick_chat',
    displayName: '快速响应模型',
    description: '为快速响应切换轻量模型（提示词在「快速响应」区域配置）',
    group: 'models',
    configPath: 'hooks.quick_chat.enabled',
    linkedTab: 'basic',
    linkedSection: 'models',
    isVirtual: false,
    directToggle: true,
    icon: 'Zap',
    order: 5,
  },
  vision: {
    id: 'vision',
    displayName: '视觉模型',
    description: '图像理解与多模态对话',
    group: 'models',
    configPath: 'hooks.vision.enabled',  // New: unified enabled pattern
    linkedTab: 'basic',
    linkedSection: 'models_vision',
    isVirtual: false,  // Now has backend hook
    directToggle: true,  // Can toggle directly
    icon: 'Eye',
    order: 10,
  },
  reasoning: {
    id: 'reasoning',
    displayName: '推理能力',
    description: '扩展思考/思维链(CoT)',
    group: 'models',
    configPath: 'hooks.reasoning.enabled',  // New: unified enabled pattern
    linkedTab: 'basic',
    linkedSection: 'models_reasoning',
    isVirtual: false,  // Now has backend hook
    directToggle: true,  // Can toggle directly
    icon: 'Lightbulb',
    order: 20,
  },
  reranking: {
    id: 'reranking',
    displayName: '重排序',
    description: '搜索结果重排序优化',
    group: 'models',
    configPath: 'hooks.reranking.enabled',  // New: unified enabled pattern
    linkedTab: 'basic',
    linkedSection: 'models_reranking',
    isVirtual: false,  // Now has backend hook
    directToggle: true,  // Can toggle directly
    icon: 'ArrowUpDown',
    order: 25,
  },
  stt: {
    id: 'stt',
    displayName: '语音输入',
    description: '语音转文字',
    group: 'models',
    configPath: 'hooks.stt.enabled',  // New: unified enabled pattern
    linkedTab: 'basic',
    linkedSection: 'models_stt',
    isVirtual: false,  // Now has backend hook
    directToggle: true,  // Can toggle directly
    icon: 'Mic',
    order: 30,
  },

  // ===== Memory (记忆系统) =====
  memory_save: {
    id: 'memory_save',
    displayName: '记忆保存',
    description: '对话结束后自动提取并保存重要信息到长期记忆（写入）',
    group: 'memory',
    configPath: 'hooks.memory_save.enabled',
    directToggle: true,
    icon: 'Save',
    order: 10,
  },
  user_facts: {
    id: 'user_facts',
    displayName: '用户画像',
    description: '加载用户的偏好、习惯等永久性信息到系统指令（读取）',
    group: 'memory',
    configPath: 'hooks.user_facts.enabled',
    linkedTab: 'memory',
    linkedSection: 'memory_query',
    directToggle: true,
    icon: 'UserCircle',
    order: 20,
    enhancesPrompt: true,
  },
  memory_context: {
    id: 'memory_context',
    displayName: '记忆检索',
    description: '根据当前话题向量搜索相关的事件、计划等时态性记忆（搜索）',
    group: 'memory',
    configPath: 'hooks.memory_context.enabled',
    linkedTab: 'memory',
    linkedSection: 'memory_query',
    directToggle: true,
    dependents: ['memory_reinforce'],
    icon: 'Search',
    order: 30,
    enhancesPrompt: true,
  },
  memory_reinforce: {
    id: 'memory_reinforce',
    displayName: '记忆强化',
    description: '基于艾宾浩斯遗忘曲线，智能复习和强化重要记忆',
    group: 'memory',
    configPath: 'hooks.memory_reinforce.enabled',
    linkedTab: 'memory',
    linkedSection: 'memory_ebbinghaus',
    directToggle: true,
    requires: ['memory_context'],
    icon: 'Repeat',
    order: 40,
  },

  // ===== Astrology (占星能力) =====
  astrology_plus: {
    id: 'astrology_plus',
    displayName: '占星增强',
    description: '本命盘计算、行运预测、星座配对等专业占星功能',
    group: 'astrology',
    configPath: 'hooks.astrology_plus.enabled',
    linkedTab: 'astrology',
    directToggle: true,
    icon: 'Star',
    order: 10,
    enhancesPrompt: true,
  },

  // ===== Skills (技能) =====
  classifier: {
    id: 'classifier',
    displayName: '技能匹配',
    description: '智能分析用户意图，匹配最佳技能策略',
    group: 'skills',
    configPath: 'hooks.classifier.enabled',
    linkedTab: 'classifier',
    linkedSection: 'response_classifier',
    directToggle: true,
    icon: 'Filter',
    order: 10,
    enhancesPrompt: true,
  },
  skill_tracking: {
    id: 'skill_tracking',
    displayName: '技能追踪',
    description: '记录技能执行历史，追踪工具使用情况和效果',
    group: 'skills',
    configPath: 'hooks.skill_tracking.enabled',
    directToggle: true,
    dependents: ['skill_dedup'],
    icon: 'Activity',
    order: 20,
  },
  skill_dedup: {
    id: 'skill_dedup',
    displayName: '技能去重',
    description: '避免在同一对话中重复执行相同技能，提升效率',
    group: 'skills',
    configPath: 'hooks.skill_dedup.enabled',
    directToggle: true,
    requires: ['skill_tracking'],
    icon: 'Layers',
    order: 30,
    enhancesPrompt: true,
  },

  // ===== Knowledge (知识检索) =====
  knowledge_context: {
    id: 'knowledge_context',
    displayName: '知识库检索',
    description: 'RAG 检索增强生成，从知识库中获取专业信息辅助回答',
    group: 'knowledge',
    configPath: 'hooks.knowledge_context.enabled',
    linkedTab: 'knowledge',
    directToggle: true,
    icon: 'BookOpen',
    order: 10,
    enhancesPrompt: true,
  },

  // ===== Advanced (高级选项) =====
  first_message: {
    id: 'first_message',
    displayName: '首消息指令',
    description: '自定义新对话开始时的首条系统指令或问候语',
    group: 'advanced',
    configPath: 'hooks.first_message.enabled',
    linkedTab: 'advanced',
    linkedSection: 'first_message',
    directToggle: true,
    icon: 'MessageSquare',
    order: 10,
    enhancesPrompt: true,
  },
  team: {
    id: 'team',
    displayName: '团队模式',
    description: '启用多 Agent 协作，让不同专长的 AI 共同完成任务',
    group: 'advanced',
    configPath: 'agent_mode',
    isVirtual: true,
    directToggle: true,
    icon: 'Users',
    order: 20,
  },
  debug: {
    id: 'debug',
    displayName: '调试模式',
    description: '在响应中显示详细的执行过程、工具调用和推理信息',
    group: 'advanced',
    configPath: 'debug_mode',
    isVirtual: true,
    directToggle: true,
    icon: 'Bug',
    order: 30,
  },
};

// ============= Config Presets =============

export const CONFIG_PRESETS: Record<SelectablePresetId, ConfigPreset> = {
  minimal: {
    id: 'minimal',
    name: '极简模式',
    description: '仅基础对话功能，最轻量启动',
    isDefault: true,
    enabledHooks: ['storage', 'memory_save', 'quick_chat'],
  },
  standard: {
    id: 'standard',
    name: '标准模式',
    description: '个性化记忆 + 占星能力',
    enabledHooks: ['storage', 'memory_save', 'quick_chat', 'user_facts', 'memory_context', 'astrology_plus'],
  },
  advanced: {
    id: 'advanced',
    name: '高级模式',
    description: '全部功能启用（团队模式除外）',
    enabledHooks: [
      // Core
      'storage',
      'quick_chat',
      'datetime',
      'device',
      'location',
      'emotion_enhance',
      // Memory
      'memory_save',
      'user_facts',
      'memory_context',
      'memory_reinforce',
      // Astrology
      'astrology_plus',
      // Skills
      'classifier',
      'skill_tracking',
      'skill_dedup',
      // Knowledge
      'knowledge_context',
      // Advanced
      'first_message',
      'debug',
      // Note: 'team' is excluded - should be toggled independently
    ],
  },
};

// ============= Derived Utilities =============

/**
 * 获取所有功能组（按 order 排序）
 */
export function getFeatureGroupsOrdered(): FeatureGroup[] {
  return Object.values(FEATURE_GROUPS).sort((a, b) => a.order - b.order);
}

/**
 * 获取指定功能组下的所有 hooks（按 order 排序）
 */
export function getHooksByGroup(groupId: FeatureGroupId): HookDefinition[] {
  return Object.values(HOOKS_REGISTRY)
    .filter((h) => h.group === groupId)
    .sort((a, b) => (a.order ?? 100) - (b.order ?? 100));
}

/**
 * 获取 Hook 定义
 */
export function getHook(hookId: HookId): HookDefinition | undefined {
  return HOOKS_REGISTRY[hookId];
}

/**
 * 获取功能组定义
 */
export function getFeatureGroup(groupId: FeatureGroupId): FeatureGroup | undefined {
  return FEATURE_GROUPS[groupId];
}

/**
 * 获取预设配置
 */
export function getPreset(presetId: PresetId): ConfigPreset | undefined {
  if (presetId === 'custom') return undefined;
  return CONFIG_PRESETS[presetId];
}

/**
 * 获取默认预设
 */
export function getDefaultPreset(): ConfigPreset {
  return Object.values(CONFIG_PRESETS).find((p) => p.isDefault) || CONFIG_PRESETS.minimal;
}

// ============= Mapping Utilities =============

/**
 * 获取 Hook ID 到 Tab ID 的映射
 * 替代 HooksPipeline.tsx 中的 HOOK_TO_TAB_MAPPING
 */
export function getHookToTabMap(): Record<string, TabId> {
  return Object.fromEntries(
    Object.values(HOOKS_REGISTRY)
      .filter((h) => h.linkedTab)
      .map((h) => [h.id, h.linkedTab as TabId])
  );
}

/**
 * 获取 Tab ID 到 Hook ID 的映射
 * 替代 AgentConfigPage.tsx 中的 TAB_TO_HOOK_NAME
 */
export function getTabToHookMap(): Record<TabId, HookId | null> {
  const result: Record<string, HookId | null> = {};
  for (const hook of Object.values(HOOKS_REGISTRY)) {
    if (hook.linkedTab) {
      result[hook.linkedTab] = hook.id;
    }
  }
  return result as Record<TabId, HookId | null>;
}

/**
 * 获取配置路径到 Tab ID 的映射
 * 替代 AgentConfigPage.tsx 中的 FEATURE_PATH_TO_TAB
 */
export function getConfigPathToTabMap(): Record<string, TabId> {
  return Object.fromEntries(
    Object.values(HOOKS_REGISTRY)
      .filter((h) => h.linkedTab)
      .map((h) => [h.configPath, h.linkedTab as TabId])
  );
}

/**
 * 获取 Tab ID 到功能配置的映射
 * 替代 AgentConfigPage.tsx 中的 TAB_TO_FEATURE_CONFIG
 */
export function getTabToFeatureConfigMap(): Record<TabId, { path: string; name: string; hookId: HookId } | null> {
  const result: Record<string, { path: string; name: string; hookId: HookId } | null> = {};
  for (const hook of Object.values(HOOKS_REGISTRY)) {
    if (hook.linkedTab) {
      result[hook.linkedTab] = {
        path: hook.configPath,
        name: hook.displayName,
        hookId: hook.id,
      };
    }
  }
  return result as Record<TabId, { path: string; name: string; hookId: HookId } | null>;
}

// ============= Dependency Utilities =============

/**
 * 获取 Hook 依赖关系
 * 替代 HooksPipeline.tsx 中的 HOOK_DEPENDENTS 和 HOOK_REQUIRES
 */
export function getHookDependencies(): {
  requires: Record<HookId, HookId[]>;
  dependents: Record<HookId, HookId[]>;
} {
  const requires: Record<string, HookId[]> = {};
  const dependents: Record<string, HookId[]> = {};

  for (const hook of Object.values(HOOKS_REGISTRY)) {
    if (hook.requires && hook.requires.length > 0) {
      requires[hook.id] = hook.requires;
    }
    if (hook.dependents && hook.dependents.length > 0) {
      dependents[hook.id] = hook.dependents;
    }
  }

  return {
    requires: requires as Record<HookId, HookId[]>,
    dependents: dependents as Record<HookId, HookId[]>,
  };
}

/**
 * 检查 hook 是否可以被禁用（检查是否有依赖它的 hook 正在启用）
 */
export function checkCanDisable(hookId: HookId, enabledHooks: Set<HookId>): { canDisable: boolean; blockedBy: HookId[] } {
  const hook = HOOKS_REGISTRY[hookId];
  if (!hook || !hook.dependents) {
    return { canDisable: true, blockedBy: [] };
  }

  const blockedBy = hook.dependents.filter((depId) => enabledHooks.has(depId));
  return {
    canDisable: blockedBy.length === 0,
    blockedBy,
  };
}

/**
 * 检查 hook 是否可以被启用（检查是否缺少依赖的 hook）
 */
export function checkCanEnable(hookId: HookId, enabledHooks: Set<HookId>): { canEnable: boolean; missingDeps: HookId[] } {
  const hook = HOOKS_REGISTRY[hookId];
  if (!hook || !hook.requires) {
    return { canEnable: true, missingDeps: [] };
  }

  const missingDeps = hook.requires.filter((reqId) => !enabledHooks.has(reqId));
  return {
    canEnable: missingDeps.length === 0,
    missingDeps,
  };
}

// ============= Feature Toggle Utilities =============

/**
 * 检查功能是否启用（处理特殊值）
 * 替代 featureHooks.ts 中的 isFeatureEnabled
 *
 * Model capability hooks (vision, stt, reasoning, reranking) now use
 * unified hooks.*.enabled pattern - simple boolean check.
 */
export function isHookEnabled(hookId: HookId, value: unknown): boolean {
  switch (hookId) {
    case 'team':
      return value === 'team';
    // Model capability hooks now use simple boolean enabled flag
    case 'quick_chat':
    case 'vision':
    case 'stt':
    case 'reasoning':
    case 'reranking':
      return Boolean(value);
    default:
      return Boolean(value);
  }
}

/**
 * 获取切换功能时应设置的值
 * 替代 featureHooks.ts 中的 getFeatureToggleValue
 *
 * Model capability hooks (quick_chat, vision, stt, reasoning, reranking) now use
 * unified hooks.*.enabled pattern - simple boolean toggle.
 */
export function getToggleValue(hookId: HookId, enabled: boolean): unknown {
  switch (hookId) {
    case 'team':
      return enabled ? 'team' : 'single';
    // Model capability hooks now use simple boolean enabled flag
    case 'quick_chat':
    case 'vision':
    case 'stt':
    case 'reasoning':
    case 'reranking':
      return enabled;
    default:
      return enabled;
  }
}

// ============= Preset Utilities =============

/**
 * 根据当前启用的 hooks 推断配置预设
 */
export function inferPresetFromConfig(enabledHooks: Set<HookId>): PresetId {
  // 检查是否匹配高级模式（检查预设定义的 hooks，不含 team）
  const advancedHooks = CONFIG_PRESETS.advanced.enabledHooks;
  const matchesAdvanced =
    advancedHooks.every((h) => enabledHooks.has(h)) &&
    // 允许额外启用 team，但不允许启用太多其他
    enabledHooks.size >= advancedHooks.length &&
    enabledHooks.size <= advancedHooks.length + 1; // +1 for team
  if (matchesAdvanced) {
    return 'advanced';
  }

  // 检查是否匹配标准模式
  const standardHooks = CONFIG_PRESETS.standard.enabledHooks;
  const matchesStandard =
    standardHooks.every((h) => enabledHooks.has(h)) &&
    enabledHooks.size <= standardHooks.length + 2; // 允许少量额外启用
  if (matchesStandard) {
    return 'standard';
  }

  // 检查是否匹配极简模式
  const minimalHooks = CONFIG_PRESETS.minimal.enabledHooks;
  const matchesMinimal = enabledHooks.size <= minimalHooks.length;
  if (matchesMinimal) {
    return 'minimal';
  }

  // 不匹配任何预设 → 自定义配置
  return 'custom';
}

/**
 * 获取预设配置应该启用的 hooks 配置对象
 */
export function getPresetHooksConfig(presetId: SelectablePresetId): Record<string, boolean | string> {
  const preset = CONFIG_PRESETS[presetId];
  const config: Record<string, boolean | string> = {};

  for (const hook of Object.values(HOOKS_REGISTRY)) {
    const isEnabled = preset.enabledHooks.includes(hook.id);
    const value = getToggleValue(hook.id, isEnabled);
    config[hook.configPath] = value as boolean | string;
  }

  return config;
}

// ============= Compatibility Layer (for featureHooks.ts migration) =============

/**
 * FeatureHook 类型别名（兼容 featureHooks.ts）
 * @deprecated 使用 HookDefinition 替代
 */
export type FeatureHook = HookDefinition;

/**
 * FeatureHookId 类型别名（兼容 featureHooks.ts）
 * @deprecated 使用 HookId 替代
 */
export type FeatureHookId = HookId;

/**
 * 获取所有 hooks 作为数组（兼容 featureHooks.ts 的 FEATURE_HOOKS）
 * @deprecated 使用 HOOKS_REGISTRY 对象或 getHooksByGroup 替代
 */
export function getFeatureHooks(): HookDefinition[] {
  return Object.values(HOOKS_REGISTRY).sort((a, b) => (a.order ?? 100) - (b.order ?? 100));
}

/**
 * 按分类获取 hooks（兼容 featureHooks.ts）
 * 这里将 group 映射到 category 概念
 * @deprecated 使用 getHooksByGroup 替代
 */
export function getFeatureHooksByCategory(): Record<string, HookDefinition[]> {
  const groups: Record<string, HookDefinition[]> = {};

  for (const hook of Object.values(HOOKS_REGISTRY)) {
    // Map groups to legacy category names
    let category = 'extension';
    if (hook.group === 'core') category = 'core';
    else if (hook.group === 'advanced' && hook.id === 'debug') category = 'debug';

    if (!groups[category]) {
      groups[category] = [];
    }
    groups[category].push(hook);
  }

  // Sort each group by order
  for (const category of Object.keys(groups)) {
    groups[category].sort((a, b) => (a.order ?? 100) - (b.order ?? 100));
  }

  return groups;
}

/**
 * 获取 hook 定义（兼容 featureHooks.ts）
 * @deprecated 使用 getHook 替代
 */
export function getFeatureHook(id: HookId): HookDefinition | undefined {
  return HOOKS_REGISTRY[id];
}

/**
 * 检查功能是否启用（兼容 featureHooks.ts）
 * @deprecated 使用 isHookEnabled 替代
 */
export function isFeatureEnabled(id: HookId, value: unknown): boolean {
  return isHookEnabled(id, value);
}

/**
 * 获取功能切换值（兼容 featureHooks.ts）
 * @deprecated 使用 getToggleValue 替代
 */
export function getFeatureToggleValue(id: HookId, enabled: boolean): unknown {
  return getToggleValue(id, enabled);
}

/**
 * 获取可展开的功能列表（兼容 featureHooks.ts）
 * @deprecated 使用 getHooksByGroup 和 linkedTab 判断
 */
export function getExpandableFeatures(): HookDefinition[] {
  return Object.values(HOOKS_REGISTRY).filter((f) => f.linkedTab && !f.directToggle);
}

/**
 * 获取功能关联的 Tab（兼容 featureHooks.ts）
 * @deprecated 直接使用 hook.linkedTab
 */
export function getLinkedTab(id: HookId): TabId | undefined {
  const hook = HOOKS_REGISTRY[id];
  return hook?.linkedTab;
}

/**
 * FEATURE_HOOKS 常量（兼容 featureHooks.ts）
 * @deprecated 使用 HOOKS_REGISTRY 替代
 */
export const FEATURE_HOOKS = getFeatureHooks();

/**
 * Merged hooks registry with generated model hooks
 * Generated hooks provide displayName and description from backend schema
 * Manual definitions take precedence for other fields (icon, order, etc.)
 */
export const MERGED_HOOKS_REGISTRY: Record<HookId, HookDefinition> = {
  ...HOOKS_REGISTRY,
  // Merge generated model hooks - they provide updated displayName/description
  // but we keep our manual configPath, icon, order, etc.
  ...(Object.fromEntries(
    Object.entries(GENERATED_MODEL_HOOKS).map(([id, genHook]) => {
      const manualHook = HOOKS_REGISTRY[id as HookId];
      if (manualHook) {
        // Merge: keep manual fields, use generated displayName/description
        return [id, {
          ...manualHook,
          displayName: genHook.displayName,
          description: genHook.description,
        }];
      }
      return [id, genHook];
    })
  ) as Partial<Record<HookId, HookDefinition>>),
};

export default HOOKS_REGISTRY;
